/**
 * Modular Inventory Component
 * Main inventory component using the new modular architecture
 * Aligned with backend modular inventory system
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import useUserPreferences from '../../hooks/useUserPreferences';
import ErrorBoundary from '../common/ErrorBoundary';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import EnhancedServiceForm from './EnhancedServiceForm';
import ProductTypeForm from './inventory.types/components/ProductTypeForm';
import ItemForm from './ItemForm';
import ServiceDetailsModal from './ServiceDetailsModal';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  CubeIcon,
  TagIcon,
  WrenchIcon,
  ClockIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  LinkIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

// Import sub-modules
import { InventoryItemsManager, useInventoryItems } from './inventory.items';
import { useProductTypes } from './inventory.types/hooks/useProductTypes';
import { useServices } from './inventory.services/hooks/useServices';
import { ActivityTimeline, ActivitySummary, useInventoryHistory } from './inventory.history';
// Will import these as we create them:
// import { BulkActions } from './inventory.bulk';

const ModularInventory = () => {
  const { isAuthenticated, canManageInventory, token } = useAuth();
  const { preferences, updateInventoryTabOrder, loading: preferencesLoading } = useUserPreferences();

  // Module visibility settings
  const [moduleSettings, setModuleSettings] = useState({
    module_inventory_items_enabled: true,
    module_inventory_services_enabled: true,
  });
  const [moduleSettingsLoading, setModuleSettingsLoading] = useState(true);

  // Default tab order - memoized to prevent useEffect dependency changes
  const defaultTabOrder = useMemo(() => ['items', 'types', 'services', 'history'], []);

  // Drag and drop state - now using server-side preferences
  const [tabOrder, setTabOrder] = useState(defaultTabOrder);
  const [activeTab, setActiveTab] = useState('services'); // Set to services tab by default
  const [draggedTab, setDraggedTab] = useState(null);
  const [dragOverTab, setDragOverTab] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  const [hasInitializedActiveTab, setHasInitializedActiveTab] = useState(false);
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalItemData, setOriginalItemData] = useState({});
  const [originalServiceData, setOriginalServiceData] = useState({});
  
  // Form state management
  const [showProductTypeForm, setShowProductTypeForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showServiceSettings, setShowServiceSettings] = useState(false);
  const [showServiceDetails, setShowServiceDetails] = useState(false);
  const [editingProductType, setEditingProductType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [activeSettingsTab, setActiveSettingsTab] = useState('rsvps');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSubmittingService, setIsSubmittingService] = useState(false);

  // Form data state
  const [itemFormData, setItemFormData] = useState({
    productTypeId: '',
    productVariantId: '',
    itemNumber: '',
    serialNumber: '',
    condition: '',
    location: '',
    notes: '',
    isBulkAdd: false,
    bulkQuantity: 1,
    bulkGeneratedIds: []
  });

  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    description: '',
    rate: '',
    rateType: 'PER_PERSON',
    location: '',
    photo: null,
    showPaymentOptions: true,
    notes: '',
    availability: [
      {
        day: 'monday',
        isAvailable: true,
        startTime: '09:00',
        endTime: '17:00',
        slots: []
      }
    ],
    paymentMethods: {
      cash: false,
      card: false,
      venmo: false,
      cashApp: false,
      zelle: false,
      check: false,
      other: false
    },
    isAvailable: true,
    rsvpSettings: {},
    // Enhanced session management
    enableSessions: false,
    sessions: [],
    discountSettings: {},
    bundleDiscount: ''
  });
  
  // Use the unsaved changes hook - active when any modal is open
  useUnsavedChanges(hasUnsavedChanges, showProductTypeForm || showItemForm || showServiceForm);

  // Fetch module visibility settings
  useEffect(() => {
    const fetchModuleSettings = async () => {
      try {
        const response = await axios.get('/api/settings/module-visibility', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setModuleSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching module visibility settings:', error);
        // Keep defaults on error
      } finally {
        setModuleSettingsLoading(false);
      }
    };

    if (token) {
      fetchModuleSettings();
    }
  }, [token]);

  // Filter tab order based on module visibility settings
  const visibleTabs = useMemo(() => {
    let filtered = [...defaultTabOrder];

    // Remove items and types tabs if Items & Product Types module is disabled
    if (!moduleSettings.module_inventory_items_enabled) {
      filtered = filtered.filter(tab => tab !== 'items' && tab !== 'types');
    }

    // Remove services tab if Services module is disabled
    if (!moduleSettings.module_inventory_services_enabled) {
      filtered = filtered.filter(tab => tab !== 'services');
    }

    return filtered;
  }, [defaultTabOrder, moduleSettings]);

  // Update tab order when preferences are loaded from server
  useEffect(() => {
    if (preferences.inventoryTabOrder) {
      // Filter preferences based on visible tabs
      const filteredPreferences = preferences.inventoryTabOrder.filter(tab => visibleTabs.includes(tab));
      setTabOrder(filteredPreferences.length > 0 ? filteredPreferences : visibleTabs);

      // Only update activeTab if it's not already set or if current tab is not in new order
      if (!activeTab || !filteredPreferences.includes(activeTab)) {
        setActiveTab(filteredPreferences[0] || visibleTabs[0]);
      }
    }
  }, [preferences.inventoryTabOrder, activeTab, visibleTabs]);

  // Helper function to handle variants update/creation/deletion
  const handleVariantsUpdate = async (variants, productTypeId) => {
    const results = { success: [], errors: [], skipped: [] };

    if (!Array.isArray(variants)) {
      return results;
    }
    
    // Get existing variants
    let existingVariants = [];
    try {
      const response = await axios.get(`/api/inventory/product-types/${productTypeId}/variants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      existingVariants = response.data || [];
    } catch (error) {
      console.error('Failed to fetch existing variants:', error);
    }
    
    // Process each variant
    for (const variant of variants) {
      if (!variant.name?.trim()) {
        continue; // Skip empty variants
      }
      
      try {
        // Skip variants with empty rates
        if (!variant.rate || variant.rate.trim() === '') {
          results.skipped.push(variant);
          continue;
        }

        const variantData = {
          name: variant.name.trim(),
          // Send as rates array format for flexible backend handling
          rates: [{
            rate: parseFloat(variant.rate),
            rateType: variant.rateType || 'HOURLY'
          }]
          // Note: organizationId is provided by authentication middleware, not request body
        };

        if (variant.id) {
          // Update existing variant
          await axios.put(`/api/inventory/product-variants/${variant.id}`, variantData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          results.success.push(variant);
        } else {
          // Create new variant
          await axios.post(`/api/inventory/product-types/${productTypeId}/variants`, variantData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          results.success.push(variant);
        }
      } catch (error) {
        console.error('Failed to save variant:', error);
        results.errors.push({ variant, error: error.message });
      }
    }
    
    // Delete removed variants
    const formVariantIds = variants.filter(v => v.id).map(v => v.id);
    for (const existing of existingVariants) {
      if (!formVariantIds.includes(existing.id)) {
        try {
          await axios.delete(`/api/inventory/product-variants/${existing.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Failed to delete variant:', error);
          results.errors.push({ variant: existing, error: error.message });
        }
      }
    }
    
    return results;
  };
  
  // Track changes to form data
  useEffect(() => {
    if (showItemForm) {
      const hasChanges = JSON.stringify(itemFormData) !== JSON.stringify(originalItemData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [itemFormData, originalItemData, showItemForm]);
  
  useEffect(() => {
    if (showServiceForm) {
      const hasChanges = JSON.stringify(serviceFormData) !== JSON.stringify(originalServiceData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [serviceFormData, originalServiceData, showServiceForm]);

  // Add button handlers
  const handleAddProductType = () => {
    setEditingProductType(null);
    setShowProductTypeForm(true);
  };

  const handleAddItem = () => {
    const newData = {
      productTypeId: '',
      productVariantId: '',
      itemNumber: '',
      serialNumber: '',
      condition: '',
      location: '',
      hourlyRate: '',
      notes: '',
      isBulkAdd: false,
      bulkQuantity: 1,
      bulkGeneratedIds: []
    };
    setItemFormData(newData);
    setOriginalItemData({ ...newData });
    setEditingItem(null);
    setHasUnsavedChanges(false);
    setShowItemForm(true);
  };

  const handleAddService = () => {
    const newData = {
      name: '',
      description: '',
      rate: '',
      rateType: 'HOURLY',
      location: '',
      photo: null,
      photoUrls: null,
      photoBaseKey: null,
      showPaymentOptions: true,
      notes: '',
      availability: [
        {
          date: '',
          startTime: '',
          endTime: '',
          slotsAvailable: 1,
          originalSlotsAvailable: 1
        }
      ],
      // Advanced settings
      paymentMethods: {},
      paymentNote: '',
      hosts: [],
      rsvpSettings: {},
      questionnaire: {},
      privacySettings: {},
      reminderSettings: {},
      communicationEmail: '',
      // Tiered pricing settings
      enableTieredPricing: false,
      pricingTiers: [],
      // Add-ons settings
      enableAddons: false,
      addons: [],
      // Enhanced session management
      enableSessions: false,
      sessions: [],
      discountSettings: {}
    };
    setServiceFormData(newData);
    setOriginalServiceData({ ...newData });
    setEditingService(null);
    setHasUnsavedChanges(false);
    setShowServiceForm(true);
  };

  // Handle product type actions
  const handleEditProductType = (productType) => {
    setEditingProductType(productType);
    setShowProductTypeForm(true);
  };

  const handleDeleteProductType = async (productType) => {
    if (window.confirm(`Are you sure you want to delete the product type "${productType.name}"? This action cannot be undone.`)) {
      try {
        await deleteProductType(productType.id);
      } catch (error) {
        console.error('Failed to delete product type:', error);
      }
    }
  };

  // Photo handling functions
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    
    if (file.size > 25 * 1024 * 1024) { // 25MB limit
      alert('File size must be less than 25MB.');
      return;
    }

    try {
      // Set uploading state
      setIsUploadingPhoto(true);
      
      // Create preview URL immediately for better UX
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);

      // Generate a temporary service ID for upload if creating new service
      const serviceId = editingService?.id || 'temp-' + Date.now();
      
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('serviceId', serviceId);
      formData.append('organizationId', 'current'); // Will be set by server middleware

      const response = await axios.post('/api/uploads/service-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.success) {
        setServiceFormData(prev => ({
          ...prev,
          photoUrls: JSON.stringify(response.data.urls), // Backend returns 'urls', store as JSON string
          photoBaseKey: response.data.baseKey,
          photo: file
        }));
        toast.success('Photo uploaded successfully!');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo. Please try again.');
      
      // Clean up preview on error
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
        setPhotoPreview(null);
      }
      
      // Reset file input
      const fileInput = event.target;
      if (fileInput) fileInput.value = '';
    } finally {
      // Always reset uploading state
      setIsUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    try {
      // If there's a base key, delete from S3
      if (serviceFormData.photoBaseKey) {
        await axios.delete(`/api/uploads/service-photo/${serviceFormData.photoBaseKey}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error deleting photo from S3:', error);
      // Continue with local cleanup even if S3 deletion fails
    }

    // Clean up local state
    setServiceFormData(prev => ({
      ...prev,
      photo: null,
      photoUrls: null,
      photoBaseKey: null
    }));
    
    // Clean up preview URL if it exists
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
    }
  };

  const resetServiceFormData = () => {
    const resetData = {
      name: '',
      description: '',
      rate: '',
      rateType: 'HOURLY',
      location: '',
      photo: null,
      photoUrls: null,
      photoBaseKey: null,
      showPaymentOptions: true,
      notes: '',
      availability: [
        {
          date: '',
          startTime: '',
          endTime: '',
          slotsAvailable: 1,
          originalSlotsAvailable: 1
        }
      ],
      // Advanced settings
      paymentMethods: {},
      paymentNote: '',
      hosts: [],
      rsvpSettings: {},
      questionnaire: {},
      privacySettings: {},
      reminderSettings: {},
      communicationEmail: '',
      // Tiered pricing settings
      enableTieredPricing: false,
      pricingTiers: [],
      // Add-ons settings
      enableAddons: false,
      addons: [],
      // Enhanced session management
      enableSessions: false,
      sessions: [],
      discountSettings: {}
    };
    setServiceFormData(resetData);
    setOriginalServiceData(resetData);
    setEditingService(null);
    setShowServiceForm(false);
    setShowServiceSettings(false);
    setPhotoPreview(null);
    setActiveSettingsTab('hosts');
    setHasUnsavedChanges(false);
  };

  const resetServiceForm = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Your changes have not been saved - are you sure you want to leave this page?')) {
        resetServiceFormData();
      }
    } else {
      const resetData = {
        name: '',
        description: '',
        rate: '',
        rateType: 'HOURLY',
        location: '',
        photo: null,
        photoUrls: null,
        photoBaseKey: null,
        showPaymentOptions: true,
        notes: '',
        availability: [
          {
            date: '',
            startTime: '',
            endTime: '',
            slotsAvailable: 1,
            originalSlotsAvailable: 1
          }
        ],
        // Advanced settings
        paymentMethods: {},
        paymentNote: '',
        hosts: [],
        rsvpSettings: {},
        questionnaire: {},
        privacySettings: {},
        reminderSettings: {},
        // Tiered pricing settings
        enableTieredPricing: false,
        pricingTiers: [],
        // Add-ons settings
        enableAddons: false,
        addons: [],
        // Enhanced session management
        enableSessions: false,
        sessions: [],
        discountSettings: {}
      };
      setServiceFormData(resetData);
      setOriginalServiceData(resetData);
      setEditingService(null);
      setShowServiceForm(false);
      setShowServiceSettings(false);
      setPhotoPreview(null);
      setActiveSettingsTab('hosts');
      setHasUnsavedChanges(false);
    }
  };

  // Handle service actions
  const handleEditService = (service) => {
    console.log('🔍 ModularInventory handleEditService called - opening edit form');
    console.log('🔍 Service to edit:', service);
    console.log('🔍 Service sessions (raw):', service.sessions);
    console.log('🔍 Service enableSessions:', service.enableSessions);
    console.log('🔍 Service sessions type:', typeof service.sessions);
    console.log('🔍 Service sessions length:', service.sessions?.length);
    console.log('🔍 Service enableTieredPricing:', service.enableTieredPricing, 'type:', typeof service.enableTieredPricing);

    const formData = {
      name: service.name || '',
      description: service.description || '',
      rate: service.rate || '',
      rateType: service.rateType || 'HOURLY',
      location: service.location || '',
      photo: service.photo || null,
      photoUrls: service.photoUrls || null,
      photoBaseKey: service.photoBaseKey || null,
      showPaymentOptions: service.showPaymentOptions !== undefined ? service.showPaymentOptions : true,
      notes: service.notes || '',
      bundleDiscount: service.bundleDiscount || '',
      availability: service.availability || [
        {
          date: '',
          startTime: '',
          endTime: '',
          slotsAvailable: 1,
          originalSlotsAvailable: 1
        }
      ],
      // Advanced settings
      paymentMethods: service.paymentSettings || {},
      paymentNote: service.paymentNote || '',
      hosts: service.hosts || [],
      rsvpSettings: service.rsvpSettings || {},
      questionnaire: service.questionnaire || {},
      privacySettings: service.privacySettings || {},
      reminderSettings: service.reminderSettings || {},
      communicationEmail: service.communicationEmail || '',
      // Tiered pricing settings
      enableTieredPricing: service.enableTieredPricing !== undefined ? service.enableTieredPricing : false,
      pricingTiers: service.pricingTiers || [],
      // Add-ons settings
      enableAddons: service.enableAddons || false,
      addons: service.addons || [],
      // Enhanced session management fields
      // Auto-enable sessions if service has session data, otherwise use service setting
      enableSessions: (service.sessions && service.sessions.length > 0) ? true : (service.enableSessions || false),
      sessions: service.sessions || [],
      discountSettings: service.discountSettings || {}
    };

    console.log('🔍 ModularInventory - formData being set:', {
      enableSessions: formData.enableSessions,
      sessions: formData.sessions,
      sessionsType: typeof formData.sessions,
      sessionsLength: formData.sessions?.length,
      originalEnableSessions: service.enableSessions,
      autoEnabledDueToSessions: (service.sessions && service.sessions.length > 0)
    });

    setServiceFormData(formData);
    setOriginalServiceData({ ...formData }); // Fixed: use formData instead of serviceFormData
    setEditingService(service);
    setHasUnsavedChanges(false);
    
    // Simplified photo preview - backend now provides clean URLs
    // Set photo preview - prioritize S3 URLs over base64
    let photoUrl = null;

    // Priority 1: Check photoUrls.original (S3 format)
    if (service.photoUrls && typeof service.photoUrls === 'object' && service.photoUrls.original) {
      photoUrl = service.photoUrls.original;
      console.log('ModularInventory: Using S3 URL from photoUrls.original:', photoUrl);
    }
    // Priority 2: Check if photoUrls is a string (JSON format)
    else if (service.photoUrls && typeof service.photoUrls === 'string') {
      try {
        const urls = JSON.parse(service.photoUrls);
        if (urls && urls.original) {
          photoUrl = urls.original;
          console.log('ModularInventory: Parsed S3 URL from photoUrls string:', photoUrl);
        }
      } catch (e) {
        console.warn('ModularInventory: Failed to parse photoUrls JSON:', e.message);
      }
    }
    // Priority 3: Use service.photo (fallback for legacy data)
    else if (service.photo) {
      photoUrl = service.photo;
      console.log('ModularInventory: Using legacy service.photo:', photoUrl);
    }

    setPhotoPreview(photoUrl);
    console.log('ModularInventory: Final photo preview set to:', photoUrl ? 'URL present' : 'null');
    
    setShowServiceForm(true);
  };

  const handleDeleteService = async (service) => {
    if (window.confirm(`Are you sure you want to delete the service "${service.name}"? This action cannot be undone.`)) {
      try {
        await deleteService(service.id);
      } catch (error) {
        console.error('Failed to delete service:', error);
      }
    }
  };

  const handleViewService = (service) => {
    setSelectedService(service);
    setShowServiceDetails(true);
  };
  
  const generateShareLink = async (serviceId) => {
    try {
      const response = await axios.post(`/api/inventory/services/${serviceId}/generate-share-link`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const shareToken = response.data.shareToken;
      const shareLink = `${window.location.origin}/service/${shareToken}`;
      
      // Update the selected service with the new share token
      setSelectedService(prev => prev ? { ...prev, shareToken } : null);
      
      // Also update the services list - need to refetch from hook
      // This will trigger a re-fetch of services
      await fetchServices();

      toast.success('Share link generated successfully!');
      return shareLink;
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
      throw error;
    }
  };

  const handleViewShareLink = (service) => {
    if (service.shareToken) {
      const shareUrl = `${window.location.origin}/service/${service.shareToken}`;
      // Could open a modal or copy to clipboard - for now just show alert
      alert(`Share link: ${shareUrl}`);
    }
  };

  // Track which tabs have been loaded
  const [loadedTabs, setLoadedTabs] = useState(new Set());

  // Sub-module hooks - disable auto-fetch for lazy loading
  const {
    items,
    loading: itemsLoading,
    error: itemsError,
    fetchItems,
    createItem,
    updateItem,
    totalItems,
    availableCount,
    inUseCount,
    maintenanceCount
  } = useInventoryItems({ autoFetch: false });

  const {
    productTypes,
    loading: typesLoading,
    error: typesError,
    fetchProductTypes,
    createProductType,
    updateProductType,
    deleteProductType,
    totalTypes
  } = useProductTypes({ autoFetch: false });

  const {
    services,
    loading: servicesLoading,
    error: servicesError,
    fetchServices,
    createService,
    updateService,
    deleteService,
    totalServices,
    activeServices
  } = useServices({ autoFetch: false });

  const {
    history,
    loading: historyLoading,
    error: historyError,
    loadNextPage,
    hasNextPage,
    activitySummary,
    fetchHistory
  } = useInventoryHistory({ autoFetch: false });

  // Lazy load data for active tab
  useEffect(() => {
    if (!activeTab || !token) return;

    // Only fetch if tab hasn't been loaded yet
    if (!loadedTabs.has(activeTab)) {
      console.log(`🚀 Lazy loading data for tab: ${activeTab}`);

      switch (activeTab) {
        case 'items':
          fetchItems?.();
          break;
        case 'types':
          fetchProductTypes?.();
          break;
        case 'services':
          fetchServices?.();
          break;
        case 'history':
          fetchHistory?.();
          break;
      }

      // Mark tab as loaded
      setLoadedTabs(prev => new Set(prev).add(activeTab));
    }
  }, [activeTab, token, loadedTabs, fetchItems, fetchProductTypes, fetchServices, fetchHistory]);

  // Combined loading state - only check loading for active tab
  const getActiveTabLoading = () => {
    switch (activeTab) {
      case 'items': return itemsLoading;
      case 'types': return typesLoading;
      case 'services': return servicesLoading;
      case 'history': return historyLoading;
      default: return false;
    }
  };

  const isLoading = getActiveTabLoading() || preferencesLoading || moduleSettingsLoading || !activeTab;
  const hasError = itemsError || typesError || servicesError || historyError;

  // Sync tab order with user preferences and set initial active tab
  useEffect(() => {
    if (!preferencesLoading && !moduleSettingsLoading) {
      const finalTabOrder = preferences.inventoryTabOrder
        ? preferences.inventoryTabOrder.filter(tab => visibleTabs.includes(tab))
        : visibleTabs;

      setTabOrder(finalTabOrder.length > 0 ? finalTabOrder : visibleTabs);

      // Only set active tab to leftmost on first load, not on every preference change
      // This prevents overriding manual tab clicks
      if (!hasInitializedActiveTab) {
        const leftmostTab = finalTabOrder[0] || visibleTabs[0];
        setActiveTab(leftmostTab);
        setHasInitializedActiveTab(true);

        console.log('🔄 Inventory tab order loaded:', finalTabOrder);
        console.log('🎯 Inventory active tab set to leftmost:', leftmostTab);
      } else {
        console.log('🔄 Inventory tab order updated without changing active tab:', finalTabOrder);
      }
    }
  }, [preferences.inventoryTabOrder, preferencesLoading, moduleSettingsLoading, visibleTabs, hasInitializedActiveTab]);

  // Cleanup effect for touch timers
  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  // Save tab order to server and update local state
  const saveTabOrder = (newOrder) => {
    setTabOrder(newOrder);
    updateInventoryTabOrder(newOrder);

    // If the currently active tab is still in the new order, keep it active
    // Otherwise, switch to the leftmost tab
    if (!newOrder.includes(activeTab)) {
      setActiveTab(newOrder[0]);
      console.log('🎯 Inventory active tab switched to leftmost after reorder:', newOrder[0]);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, tabId) => {
    setDraggedTab(tabId);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tabId);

    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggedTab(null);
    setDragOverTab(null);
    setIsDragging(false);

    // Reset visual feedback
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, tabId) => {
    e.preventDefault();
    if (draggedTab !== tabId) {
      setDragOverTab(tabId);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear drag over if we're actually leaving the tab area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverTab(null);
    }
  };

  const handleDrop = (e, targetTabId) => {
    e.preventDefault();

    if (!draggedTab || draggedTab === targetTabId) {
      return;
    }

    const currentOrder = [...tabOrder];
    const draggedIndex = currentOrder.indexOf(draggedTab);
    const targetIndex = currentOrder.indexOf(targetTabId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged tab from current position
      currentOrder.splice(draggedIndex, 1);
      // Insert dragged tab at target position
      currentOrder.splice(targetIndex, 0, draggedTab);

      saveTabOrder(currentOrder);
    }

    setDragOverTab(null);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e, tabId) => {
    // Store initial touch position to detect horizontal scroll vs long press
    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
    setTouchStartY(touch.clientY);

    const timer = setTimeout(() => {
      setDraggedTab(tabId);
      setIsDragging(true);
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Add visual feedback
      e.target.style.transform = 'scale(1.05)';
      e.target.style.zIndex = '1000';
    }, 500); // 500ms long press

    setLongPressTimer(timer);
  };

  const handleTouchEnd = (e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    if (isDragging) {
      // Reset visual feedback
      e.target.style.transform = '';
      e.target.style.zIndex = '';
      setDraggedTab(null);
      setIsDragging(false);
    }
  };

  const handleTouchMove = (e, targetTabId) => {
    // If we're not dragging yet, check if this is horizontal scrolling
    if (!isDragging) {
      if (longPressTimer) {
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStartX);
        const deltaY = Math.abs(touch.clientY - touchStartY);

        // If horizontal movement is greater than vertical, it's likely scrolling
        // Or if there's any significant vertical movement, cancel drag to allow normal scrolling
        if (deltaX > deltaY && deltaX > 10) {
          clearTimeout(longPressTimer);
          setLongPressTimer(null);
          return; // Allow horizontal scrolling
        }

        // If there's significant vertical movement, also cancel drag
        if (deltaY > 15) {
          clearTimeout(longPressTimer);
          setLongPressTimer(null);
          return;
        }
      }
      return;
    }

    if (!draggedTab) return;

    e.preventDefault();

    // Get touch position
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    // Find the tab element
    const tabElement = element?.closest('[data-tab-id]');
    if (tabElement) {
      const newTargetTabId = tabElement.getAttribute('data-tab-id');
      if (newTargetTabId && newTargetTabId !== draggedTab) {
        setDragOverTab(newTargetTabId);

        // Perform the reorder
        const currentOrder = [...tabOrder];
        const draggedIndex = currentOrder.indexOf(draggedTab);
        const targetIndex = currentOrder.indexOf(newTargetTabId);

        if (draggedIndex !== -1 && targetIndex !== -1) {
          currentOrder.splice(draggedIndex, 1);
          currentOrder.splice(targetIndex, 0, draggedTab);
          saveTabOrder(currentOrder);
        }
      }
    }
  };

  // Tab configuration
  const tabConfigs = {
    items: {
      id: 'items',
      name: 'Items',
      icon: CubeIcon,
      count: totalItems,
      description: `${availableCount} available, ${inUseCount} in use, ${maintenanceCount} maintenance`
    },
    types: {
      id: 'types',
      name: 'Product Types',
      icon: TagIcon,
      count: totalTypes,
      description: `${totalTypes} types defined`
    },
    services: {
      id: 'services',
      name: 'Services',
      icon: WrenchIcon,
      count: totalServices,
      description: `${activeServices} active services`
    },
    history: {
      id: 'history',
      name: 'Activity',
      icon: ClockIcon,
      count: activitySummary.totalActions,
      description: `${activitySummary.recentActivity} recent actions`
    }
  };

  // Create ordered tabs array based on tabOrder state
  const tabs = tabOrder.map(tabId => tabConfigs[tabId]).filter(Boolean);

  // Handle item actions
  const handleEditItem = async (item) => {
    setItemFormData({
      productTypeId: item.productTypeId || '',
      productVariantId: item.productVariantId || '',
      itemNumber: item.itemNumber || '',
      serialNumber: item.serialNumber || '',
      condition: item.condition || '',
      location: item.location || '',
      hourlyRate: item.hourlyRate || '',
      notes: item.notes || '',
      isBulkAdd: false,
      bulkQuantity: 1,
      bulkGeneratedIds: []
    });
    setOriginalItemData({ ...itemFormData });
    setEditingItem(item);
    setHasUnsavedChanges(false);
    setShowItemForm(true);
  };


  // Check authentication and permissions
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">Please log in to access inventory.</p>
        </div>
      </div>
    );
  }

  if (!canManageInventory) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Insufficient Permissions</h1>
          <p className="text-gray-600 mt-2">You don't have permission to manage inventory.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
              <p className="text-gray-600 mt-1">Manage your inventory items, types, and services</p>
            </div>
            
            {/* Add Buttons */}
            {canManageInventory && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <button
                  onClick={handleAddProductType}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center justify-center w-full sm:w-auto text-sm sm:text-base touch-friendly"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  <span className="sm:hidden">Product Type</span>
                  <span className="hidden sm:inline">Add Product Type</span>
                </button>
                <button
                  onClick={handleAddItem}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center justify-center w-full sm:w-auto text-sm sm:text-base touch-friendly disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!Array.isArray(productTypes) || productTypes.length === 0}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  <span className="sm:hidden">Item</span>
                  <span className="hidden sm:inline">Add Item</span>
                </button>
                <button
                  onClick={handleAddService}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center justify-center w-full sm:w-auto text-sm sm:text-base touch-friendly"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  <span className="sm:hidden">Service</span>
                  <span className="hidden sm:inline">Add Service</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Global Error */}
        {hasError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800">
              <strong>Error:</strong> {hasError}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Empty State for New Customers */}
        {(!Array.isArray(productTypes) || productTypes.length === 0) && (!Array.isArray(items) || items.length === 0) && (!Array.isArray(services) || services.length === 0) && (
          <div className="bg-white rounded-lg shadow p-12">
            <EmptyState
              icon={CubeIcon}
              title="Welcome to Your Inventory System"
              description="Get started by creating your first product type. This could be anything you rent out - boats, bikes, equipment, vehicles, or any other items."
              action={canManageInventory ? {
                label: 'Create Your First Product Type',
                onClick: handleAddProductType
              } : undefined}
            />
          </div>
        )}

        {/* Main Content */}
        {!isLoading && (
          <>
            {/* Tab Navigation - Mobile Scrollable */}
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <div className="overflow-x-auto overflow-y-hidden scrollbar-hide" style={{ overscrollBehaviorX: 'contain', overscrollBehaviorY: 'none' }}>
                  <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max px-4 sm:px-0">
                  {tabs.map(tab => {
                    const Icon = tab.icon;
                    const isBeingDragged = draggedTab === tab.id;
                    const isDragTarget = dragOverTab === tab.id;

                    return (
                      <button
                        key={tab.id}
                        data-tab-id={tab.id}
                        draggable={true}
                        onClick={() => !isDragging && setActiveTab(tab.id)}
                        onDragStart={(e) => handleDragStart(e, tab.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, tab.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, tab.id)}
                        onTouchStart={(e) => handleTouchStart(e, tab.id)}
                        onTouchEnd={handleTouchEnd}
                        onTouchMove={(e) => handleTouchMove(e, tab.id)}
                        className={`py-2 px-3 sm:px-1 border-b-2 font-medium text-sm flex items-center space-x-2 cursor-pointer select-none transition-all duration-200 whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } ${
                          isBeingDragged
                            ? 'opacity-50 transform scale-95'
                            : ''
                        } ${
                          isDragTarget
                            ? 'bg-blue-50 border-blue-300 scale-105'
                            : ''
                        } ${
                          isDragging && !isBeingDragged
                            ? 'opacity-75'
                            : ''
                        }`}
                        style={{
                          userSelect: 'none',
                          touchAction: 'pan-x', // Only allow horizontal panning
                          transform: 'translateZ(0)' // Force hardware acceleration for smoother scrolling
                        }}
                      >
                        <Icon className="h-5 w-5" />
                        <span>{tab.name}</span>
                        {tab.count !== null && (
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            activeTab === tab.id
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {tab.count}
                          </span>
                        )}
                        {/* Drag indicator */}
                        <div className="opacity-0 hover:opacity-50 ml-1">
                          <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 15h18v-2H3v2zm0 4h18v-2H3v2zm0-8h18V9H3v2zm0-6v2h18V5H3z"/>
                          </svg>
                        </div>
                      </button>
                    );
                  })}
                  </nav>
                </div>
              </div>
              
              {/* Tab Description */}
              <div className="mt-2 text-sm text-gray-600">
                {tabs.find(tab => tab.id === activeTab)?.description}
              </div>
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
              {activeTab === 'items' && (
                <ErrorBoundary>
                  <InventoryItemsManager
                    productTypes={productTypes}
                    onEditItem={handleEditItem}
                  />
                </ErrorBoundary>
              )}

              {activeTab === 'types' && (
                <ErrorBoundary>
                  <div className="bg-white rounded-lg shadow p-4 sm:p-6">

                    {productTypes.length === 0 ? (
                      <div className="text-center py-8 sm:py-12">
                        <TagIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Product Types</h3>
                        <p className="text-sm sm:text-base text-gray-600">Create your first product type to get started.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {productTypes.map(type => (
                          <div key={type.id} className="border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <TagIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                  <h4 className="font-medium text-gray-900 truncate">{type.name}</h4>
                                </div>
                                {type.category && (
                                  <span className="mt-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {type.category}
                                  </span>
                                )}
                                {type.description && (
                                  <p className="text-sm text-gray-500 mt-2 line-clamp-2">{type.description}</p>
                                )}
                              </div>
                              <div className="flex space-x-1 ml-2 flex-shrink-0">
                                <button
                                  onClick={() => handleEditProductType(type)}
                                  className="p-2 sm:p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded touch-manipulation"
                                  title="Edit product type"
                                  aria-label="Edit product type"
                                >
                                  <PencilIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteProductType(type)}
                                  className="p-2 sm:p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded touch-manipulation"
                                  title="Delete product type"
                                  aria-label="Delete product type"
                                >
                                  <TrashIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ErrorBoundary>
              )}

              {activeTab === 'services' && (
                <ErrorBoundary>
                  <div className="bg-white rounded-lg shadow p-6">
                    
                    {services.length === 0 ? (
                      <div className="text-center py-12">
                        <WrenchIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Services Found</h3>
                        <p className="text-gray-600">Create your first service to get started.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {services.map(service => (
                          <div key={service.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-lg font-semibold text-gray-900 truncate">{service.name}</h3>
                              <div className="flex space-x-1">
                                <button 
                                  onClick={() => handleViewService(service)}
                                  className="p-1 text-gray-400 hover:text-green-600"
                                  title="View service details"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                                <button 
                                  onClick={() => handleEditService(service)}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Edit service"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteService(service)}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                  title="Delete service"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Service Details */}
                            <div className="space-y-3">
                              {service.description && (
                                <p className="text-sm text-gray-600 line-clamp-2">{service.description}</p>
                              )}
                              
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-gray-900">
                                  ${service.rate} / {service.rateType?.replace('_', ' ').toLowerCase()}
                                </div>
                                <div className="flex items-center space-x-2">
                                  {(() => {
                                    // Check if service has future availability slots
                                    let hasAvailableSlots = false;

                                    if (service.enableSessions && service.sessions) {
                                      // Check enhanced sessions for availability
                                      const sessions = Array.isArray(service.sessions) ? service.sessions : [];
                                      hasAvailableSlots = sessions.length > 0 && sessions.some(session => {
                                        if (!session.date || !session.slotsAvailable || session.slotsAvailable <= 0) return false;

                                        // Parse date safely to avoid timezone issues
                                        const dateParts = session.date.split('-');
                                        const sessionDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0); // Reset time to start of day
                                        return sessionDate >= today;
                                      });
                                    } else {
                                      // Check regular availability slots
                                      hasAvailableSlots = service.availability && service.availability.length > 0 &&
                                        service.availability.some(slot => {
                                          if (!slot.date || !slot.slotsAvailable || slot.slotsAvailable <= 0) return false;

                                          // Parse date safely to avoid timezone issues
                                          const dateParts = slot.date.split('-');
                                          const slotDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                                          const today = new Date();
                                          today.setHours(0, 0, 0, 0); // Reset time to start of day
                                          return slotDate >= today;
                                        });
                                    }

                                    if (hasAvailableSlots) {
                                      return (
                                        <div className="flex items-center text-green-600">
                                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                                          <span className="text-xs">Available</span>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="flex items-center text-red-600">
                                          <XCircleIcon className="h-4 w-4 mr-1" />
                                          <span className="text-xs">Unavailable</span>
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              </div>
                              
                              {/* Share Link Indicator */}
                              {service.shareToken && (
                                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                  <div className="flex items-center text-blue-600">
                                    <LinkIcon className="h-4 w-4 mr-1" />
                                    <span className="text-xs">Public booking link</span>
                                  </div>
                                  <button 
                                    onClick={() => handleViewShareLink(service)}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                  >
                                    View Link
                                  </button>
                                </div>
                              )}
                              
                              {/* Location if available */}
                              {service.location && (
                                <div className="text-xs text-gray-500">
                                  📍 {service.location}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </ErrorBoundary>
              )}

              {activeTab === 'history' && (
                <ErrorBoundary>
                  <div className="space-y-6">
                    <ActivitySummary 
                      summary={activitySummary} 
                      loading={historyLoading} 
                    />
                    <ActivityTimeline
                      activities={history}
                      loading={historyLoading}
                      onLoadMore={loadNextPage}
                      hasMore={hasNextPage}
                      showFilters={true}
                    />
                  </div>
                </ErrorBoundary>
              )}
            </div>

            {/* Forms */}
            {showProductTypeForm && (
              <ProductTypeForm
                productType={editingProductType}
                onSubmit={async (formData) => {
                  try {
                    console.log('🔍 ModularInventory - Product type form submitted with data:', formData);
                    console.log('🔍 ModularInventory - Variants included:', formData.variants);

                    // Pass the complete formData including variants to the backend
                    // The repository handles variants in a single transaction
                    if (editingProductType) {
                      await updateProductType(editingProductType.id, formData);
                      toast.success('Product type updated successfully!');
                    } else {
                      await createProductType(formData);
                      toast.success('Product type created successfully!');
                    }

                    setShowProductTypeForm(false);
                    setEditingProductType(null);
                    setHasUnsavedChanges(false);

                    // Refresh product types to show new variants
                    await fetchProductTypes();
                  } catch (error) {
                    toast.error(editingProductType ? 'Failed to update product type' : 'Failed to create product type');
                    console.error('Product type save error:', error);
                  }
                }}
                onClose={() => {
                  setShowProductTypeForm(false);
                  setEditingProductType(null);
                  setHasUnsavedChanges(false);
                }}
              />
            )}
            
            {showItemForm && (
              <ItemForm
                show={showItemForm}
                isEditing={!!editingItem}
                formData={itemFormData}
                setFormData={setItemFormData}
                productTypes={productTypes}
                onSave={async (formData) => {
                  try {
                    if (editingItem) {
                      await updateItem(editingItem.id, formData);
                      toast.success('Item updated successfully!');
                    } else {
                      await createItem(formData);
                      toast.success('Item created successfully!');
                    }
                    setShowItemForm(false);
                    setEditingItem(null);
                    setHasUnsavedChanges(false);
                  } catch (error) {
                    toast.error(editingItem ? 'Failed to update item' : 'Failed to create item');
                    console.error('Item save error:', error);
                  }
                }}
                onCancel={() => {
                  setShowItemForm(false);
                  setEditingItem(null);
                  setHasUnsavedChanges(false);
                }}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            )}
            
            {showServiceForm && (
              <EnhancedServiceForm
                service={editingService}
                serviceFormData={serviceFormData}
                setServiceFormData={setServiceFormData}
                editingService={editingService}
                handleEditService={async (e) => {
                  console.log('🔍 ModularInventory handleEditService called');
                  console.log('🔍 ModularInventory - editingService.id:', editingService?.id);
                  console.log('🔍 ModularInventory - serviceFormData:', serviceFormData);
                  console.log('🔍 ModularInventory - serviceFormData.enableSessions:', serviceFormData?.enableSessions);
                  console.log('🔍 ModularInventory - serviceFormData.sessions:', serviceFormData?.sessions);

                  e.preventDefault();

                  // Prevent submission if photo is uploading or already submitting
                  if (isUploadingPhoto) {
                    toast.error('Please wait for photo upload to complete');
                    return;
                  }

                  if (isSubmittingService) {
                    return;
                  }

                  setIsSubmittingService(true);

                  try {
                    console.log('🔍 ModularInventory - calling updateService...');
                    console.log('🔍 ModularInventory - serviceFormData enableTieredPricing:', serviceFormData.enableTieredPricing, 'type:', typeof serviceFormData.enableTieredPricing);
                    await updateService(editingService.id, serviceFormData);
                    console.log('🔍 ModularInventory - updateService completed successfully');
                    toast.success('Service updated successfully!');
                    resetServiceFormData();
                  } catch (error) {
                    console.error('🔍 ModularInventory - updateService error:', error);
                    toast.error('Failed to update service');
                    console.error('Service save error:', error);
                  } finally {
                    setIsSubmittingService(false);
                  }
                }}
                handleCreateService={async (e) => {
                  e.preventDefault();

                  // Prevent submission if photo is uploading or already submitting
                  if (isUploadingPhoto) {
                    toast.error('Please wait for photo upload to complete');
                    return;
                  }

                  if (isSubmittingService) {
                    return;
                  }

                  setIsSubmittingService(true);

                  try {
                    await createService(serviceFormData);
                    toast.success('Service created successfully!');
                    resetServiceFormData();
                  } catch (error) {
                    toast.error('Failed to create service');
                    console.error('Service save error:', error);
                  } finally {
                    setIsSubmittingService(false);
                  }
                }}
                resetServiceForm={resetServiceForm}
                hasUnsavedChanges={hasUnsavedChanges}
                setHasUnsavedChanges={setHasUnsavedChanges}
                showServiceSettings={showServiceSettings}
                setShowServiceSettings={setShowServiceSettings}
                activeSettingsTab={activeSettingsTab}
                setActiveSettingsTab={setActiveSettingsTab}
                photoPreview={photoPreview}
                setPhotoPreview={setPhotoPreview}
                handlePhotoUpload={handlePhotoUpload}
                removePhoto={removePhoto}
                isUploadingPhoto={isUploadingPhoto}
                isSubmittingService={isSubmittingService}
              />
            )}
            
            {/* Service Details Modal */}
            <ServiceDetailsModal
              service={selectedService}
              isOpen={showServiceDetails}
              onClose={() => {
                setShowServiceDetails(false);
                setSelectedService(null);
              }}
              onGenerateShareLink={generateShareLink}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ModularInventory;